// The asset register (CR_20260920_Asset_Register): remove an asset from service, confirm its tag is off, put it back,
// delete a typo. All four are idempotent SECURITY DEFINER RPCs — status and the retirement columns cannot be written
// directly (assets_retire_guard) — and a refusal carries the server's own words, which the dialogs show as they are.
import type { DisposalReason, RetireStage } from '@fleetspoke/core/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { dashboardKeys } from './dashboard';
import { keys } from './queries';

export interface RetireResult {
  asset_id: string;
  stage: RetireStage;
  /** electronic tags still on it (awaiting removal) or just taken off (confirm) */
  tags?: string[];
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase().rpc(fn, {
    p_idempotency_key: crypto.randomUUID(),
    ...args,
  });
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

function useInvalidateAsset() {
  const queryClient = useQueryClient();
  return (assetId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['assets'] });
    void queryClient.invalidateQueries({ queryKey: keys.asset(assetId) });
    void queryClient.invalidateQueries({ queryKey: ['board'] });
    void queryClient.invalidateQueries({ queryKey: keys.sites });
    void queryClient.invalidateQueries({ queryKey: dashboardKeys.actions });
    void queryClient.invalidateQueries({ queryKey: dashboardKeys.stateCounts });
  };
}

export interface RetireInput {
  assetId: string;
  /** YYYY-MM-DD in the company timezone */
  at: string;
  reason: DisposalReason;
  note: string;
  /** only for a sale; null when unknown */
  proceeds: number | null;
}

/** Step 1, managers: out of every board, count and billable class from `at`. */
export function useRetireAsset() {
  const invalidate = useInvalidateAsset();
  return useMutation({
    mutationFn: (r: RetireInput) =>
      rpc<RetireResult>('retire_asset', {
        p_asset_id: r.assetId,
        p_at: r.at,
        p_note: r.note,
        p_disposal_reason: r.reason,
        p_proceeds: r.reason === 'sold' ? r.proceeds : null,
      }),
    onSuccess: (_d, r) => invalidate(r.assetId),
  });
}

/** Step 2, warehouse staff at its site (managers only when none can): the tag is off, the retirement complete. */
export function useConfirmTagRemoved() {
  const invalidate = useInvalidateAsset();
  return useMutation({
    mutationFn: (assetId: string) =>
      rpc<RetireResult>('confirm_tag_removed', { p_asset_id: assetId }),
    onSuccess: (_d, assetId) => invalidate(assetId),
  });
}

/** Put back in service, managers, while nothing has happened to it since. */
export function useUnretireAsset() {
  const invalidate = useInvalidateAsset();
  return useMutation({
    mutationFn: (assetId: string) => rpc<RetireResult>('unretire_asset', { p_asset_id: assetId }),
    onSuccess: (_d, assetId) => invalidate(assetId),
  });
}

/** Delete, managers, only an asset with no history at all (the typo case). */
export function useDeleteAsset() {
  const invalidate = useInvalidateAsset();
  return useMutation({
    mutationFn: (assetId: string) =>
      rpc<{ asset_id: string; asset_code: string; deleted: boolean }>('delete_asset', {
        p_asset_id: assetId,
      }),
    onSuccess: (_d, assetId) => invalidate(assetId),
  });
}

// ---------------------------------------------------------------------------------------------------------
// Purchase value (phase B, §5.3): asset_financials, manager-only by RLS — a plain upsert, as alert_rules is.
// Read on demand, never joined into asset_overview (A11): a null there would read as "worth nothing".
// ---------------------------------------------------------------------------------------------------------
export interface AssetFinancials {
  asset_id: string;
  company_id: string;
  value: string | number | null;
  currency: string;
  purchased_at: string | null;
  supplier_name: string | null;
  po_ref: string | null;
  warranty_until: string | null;
  note: string | null;
  /** the pre-A13 free text; shown only when supplier_name is empty */
  supplier: string | null;
}

const FINANCIAL_COLUMNS =
  'asset_id, company_id, value, currency, purchased_at, supplier_name, po_ref, warranty_until, note, supplier';

export const financialKeys = { asset: (assetId: string) => ['asset-financials', assetId] as const };

export function useAssetFinancials(assetId: string, enabled: boolean) {
  return useQuery({
    queryKey: financialKeys.asset(assetId),
    enabled: enabled && !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('asset_financials')
        .select(FINANCIAL_COLUMNS)
        .eq('asset_id', assetId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as AssetFinancials | null;
    },
  });
}

export type FinancialsInput = Omit<AssetFinancials, 'supplier'>;

export function useSaveFinancials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (f: FinancialsInput) => {
      const { error } = await supabase()
        .from('asset_financials')
        .upsert(f, { onConflict: 'asset_id' });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: (_d, f) => {
      void queryClient.invalidateQueries({ queryKey: financialKeys.asset(f.asset_id) });
      void queryClient.invalidateQueries({ queryKey: ['utilisation'] });
    },
  });
}

// ---------------------------------------------------------------------------------------------------------
// Bulk import (phase C): the preview needs the codes already registered; the import is one RPC, all or nothing
// ---------------------------------------------------------------------------------------------------------
/** Which of these codes the company already has (RLS: a manager sees every asset). Chunked to keep URLs short. */
export async function takenCodes(codes: readonly string[]): Promise<Set<string>> {
  const taken = new Set<string>();
  const unique = [...new Set(codes.filter(Boolean))];
  for (let i = 0; i < unique.length; i += 150) {
    const { data, error } = await supabase()
      .from('assets')
      .select('asset_code')
      .in('asset_code', unique.slice(i, i + 150));
    if (error) throw new Error(error.message);
    for (const r of (data ?? []) as { asset_code: string }[]) taken.add(r.asset_code);
  }
  return taken;
}

export interface ImportResult {
  ok: boolean;
  created: number;
  errors: { row: number; code: string; field: string; value?: string | null }[];
}

export function useImportAssets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: Record<string, string>[]) =>
      rpc<ImportResult>('import_assets', { p_rows: rows }),
    onSuccess: (r) => {
      if (r.ok) {
        void queryClient.invalidateQueries({ queryKey: ['assets'] });
        void queryClient.invalidateQueries({ queryKey: ['board'] });
        void queryClient.invalidateQueries({ queryKey: keys.sites });
      }
    },
  });
}
